import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import XLSX from "xlsx";
import "./AddTag.css";

const AddTag = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [vehicleType, setVehicleType] = useState("2W");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [officeList, setOfficeList] = useState([]);
  const [errors, setErrors] = useState({});
  const [isExcelUpload, setIsExcelUpload] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [fileData, setFileData] = useState([]);
  const [fileSelected, setFileSelected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch users and offices
  useEffect(() => {
    const fetchUsers = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        Swal.fire({
          icon: "warning",
          title: "Unauthorized",
          text: "Please log in to view users.",
        });
        return;
      }

      try {
        const response = await axios.get(
          `https://housys.skymarkone.in:8001/api/user/getUserSpecificOffice/${tenantName}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUsers(response.data?.data || []);
      } catch (error) {
        console.error("Error fetching users:", error.response?.data || error.message);
      }
    };

    const fetchAllOffices = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        Swal.fire({
          icon: "warning",
          title: "Unauthorized",
          text: "Please log in to view offices.",
        });
        return;
      }

      try {
        const response = await axios.get("https://housys.skymarkone.in:8001/api/getoffice", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOfficeList(response.data?.office || []);
      } catch (error) {
        console.error("Error fetching offices:", error.response?.data || error.message);
      }
    };

    if (tenantName) fetchUsers();
    fetchAllOffices();
  }, [tenantName]);

  // File upload handler
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const binaryString = event.target.result;
        const workbook = XLSX.read(binaryString, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet);
        setFileData(jsonData);
        setFileSelected(true);
      };
      reader.readAsBinaryString(file);
    }
  };

  // Validate form inputs
  const validateForm = () => {
    const newErrors = {};
    if (!tenantName) newErrors.tenantName = "Tenant is required.";
    if (!isExcelUpload && !selectedUser) newErrors.selectedUser = "User is required.";
    if (!isExcelUpload && !cardNumber) newErrors.cardNumber = "Card number is required.";
    if (!isExcelUpload && !vehicleNumber) newErrors.vehicleNumber = "Vehicle number is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    const formData = isExcelUpload
      ? {
          method: "excel",
          office_id: tenantName,
          users: fileData.map((user) => ({
            user_id: user.user_id || "",
            vehicle_type: user.vehicle_type || "4W",
            vehicle_number: user.vehicle_number || "",
            card_number: user.card_number || "",
            block_status: false,
          })),
        }
      : {
          method: "manual",
          office_id: tenantName,
          user_id: selectedUser,
          vehicle_type: vehicleType,
          vehicle_number: vehicleNumber,
          card_number: cardNumber,
          block_status: false,
        };

    console.log("Submitted Form Data:", formData);

    try {
      const token = localStorage.getItem("token");
      console.log("Data send", formData);
      const response = await axios.post("https://housys.skymarkone.in:8001/api/create-tag", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      Swal.fire({
        icon: "success",
        title: "Success",
        text: response.data.message || "Vehicle tag created successfully.",
      });

      console.log("Response Data:", response.data);

      // Reset form
      setSelectedUser("");
      setCardNumber("");
      setVehicleType("2W");
      setVehicleNumber("");
      setTenantName("");
      setErrors({});
      setFileData([]);
      setIsExcelUpload(false);
    } catch (error) {
      console.error("Submission error:", error.response?.data || error.message);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to add the tag.",
      });
    }
  };

  return (
    <Container fluid className="mt-4">
      <h2 className="mb-4">Add Tag Details</h2>
      <div className="card" style={{ minHeight: "84vh", padding: "40px", marginTop: "20px" }}>
        <Row>
          <Col xs={12}>
            <Form.Group>
              <Form.Label>Select Input Method</Form.Label>
              <Row>
                <Col xs={6}>
                  <Form.Check
                    type="radio"
                    label="Fill Form Manually"
                    name="method"
                    checked={!isExcelUpload}
                    onChange={() => setIsExcelUpload(false)}
                  />
                </Col>
                <Col xs={6}>
                  <Form.Check
                    type="radio"
                    label="Upload Excel File"
                    name="method"
                    checked={isExcelUpload}
                    onChange={() => setIsExcelUpload(true)}
                  />
                </Col>
              </Row>
            </Form.Group>
          </Col>
        </Row>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Tenant Name</Form.Label>
                <Form.Control
                  as="select"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                >
                  <option value="">Select Tenant</option>
                  {officeList.map((office) => (
                    <option key={office.office_id} value={office.office_id}>
                      {office.office_name}
                    </option>
                  ))}
                </Form.Control>
                {errors.tenantName && <Alert variant="danger">{errors.tenantName}</Alert>}
              </Form.Group>
            </Col>
          </Row>

          {isExcelUpload ? (
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Upload Excel File</Form.Label>
                  <Form.Control type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />
                </Form.Group>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>User Name</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                  >
                    <option value="">Select User</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </Form.Control>
                  {errors.selectedUser && <Alert variant="danger">{errors.selectedUser}</Alert>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Card Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Card Number"
                    value={cardNumber}
                    onChange={(e) => {
                      const input = e.target.value;
                      if (/^[a-zA-Z0-9]*$/.test(input)) {
                        setCardNumber(input);
                      }
                    }}
                  />
                  {errors.cardNumber && <Alert variant="danger">{errors.cardNumber}</Alert>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Vehicle Type</Form.Label>
                  <Form.Control
                    as="select"
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                  >
                    <option value="2W">2W</option>
                    <option value="4W">4W</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Vehicle Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Vehicle Number"
                    value={vehicleNumber}
                    onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                  />
                  {errors.vehicleNumber && <Alert variant="danger">{errors.vehicleNumber}</Alert>}
                </Form.Group>
              </Col>
            </Row>
          )}

          <Button type="submit" className="mt-2" disabled={isSubmitting}>
            Submit
          </Button>
        </Form>
      </div>
    </Container>
  );
};

export default AddTag;
